import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import LocationSelect from './LocationSelect';
import './PackiyoOrderingPage.css';

function DropZone({ label, hint, accept, file, onFile, locked }) {
  const [dragging, setDragging] = useState(false);
  const ref = useRef();

  function pick(files) {
    if (files && files[0]) onFile(files[0]);
  }

  return (
    <div
      className={`pk-dropzone ${dragging ? 'active' : ''} ${locked ? 'locked' : ''}`}
      onClick={() => !locked && ref.current?.click()}
      onDragOver={e => { e.preventDefault(); if (!locked) setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); if (!locked) pick(e.dataTransfer.files); }}
    >
      <input
        ref={ref}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={e => pick(e.target.files)}
      />
      {file ? (
        <div className="pk-dropzone-file">
          <span className="pk-file-icon">📊</span>
          <span className="pk-file-name">{file.name}</span>
          <span className="pk-file-size">{(file.size / 1024).toFixed(1)} KB</span>
          {!locked && (
            <button
              className="pk-file-clear"
              onClick={e => { e.stopPropagation(); onFile(null); }}
            >✕</button>
          )}
        </div>
      ) : (
        <>
          <div className="pk-dropzone-icon">📂</div>
          <p className="pk-dropzone-label">{label}</p>
          <p className="pk-dropzone-hint">{hint}</p>
        </>
      )}
    </div>
  );
}

export default function PackiyoOrderingPage() {
  const [orderFile,     setOrderFile]     = useState(null);
  const [locations,     setLocations]     = useState([]);
  const [locsLoading,   setLocsLoading]   = useState(true);
  const [locsError,     setLocsError]     = useState(null);
  const [detecting,     setDetecting]     = useState(false);
  const [sessionId,     setSessionId]     = useState(null);
  const [sheets,        setSheets]        = useState([]);
  const [invalidSheets, setInvalidSheets] = useState([]);
  const [locSelections, setLocSelections] = useState({});
  const [noteValues,    setNoteValues]    = useState({});  // { [sheetName]: string }
  const [orderNumbers,  setOrderNumbers]  = useState({});  // { [sheetName]: string }
  const [processing,    setProcessing]    = useState(false);
  const [results,       setResults]       = useState([]);
  const [submitState,   setSubmitState]   = useState({});  // { [packiyo_file]: 'submitting'|'done'|{error} }
  const [shopifyState,  setShopifyState]  = useState({});  // { [packiyo_file]: {running, job, error} } — job = server status
  const [error,         setError]         = useState(null);

  useEffect(() => {
    setLocsLoading(true);
    setLocsError(null);
    axios.get('/api/packiyo/locations')
      .then(r => { setLocations(r.data); setLocsLoading(false); })
      .catch(e => { setLocsError(e.response?.data?.error || e.message); setLocsLoading(false); });
  }, []);

  function clearDetection() {
    setSheets([]);
    setInvalidSheets([]);
    setSessionId(null);
    setLocSelections({});
    setNoteValues({});
    setOrderNumbers({});
    setResults([]);
    setError(null);
  }

  async function handleDetect() {
    if (!orderFile) return;
    setDetecting(true);
    setError(null);
    clearDetection();

    const form = new FormData();
    form.append('order_file', orderFile);

    try {
      const r = await axios.post('/api/packiyo/detect', form);
      setSessionId(r.data.session_id);
      setSheets(r.data.valid_sheets);
      setInvalidSheets(r.data.invalid_sheets || []);
      if (r.data.valid_sheets.length === 1 && locations.length === 1) {
        setLocSelections({ [r.data.valid_sheets[0].name]: locations[0] });
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setDetecting(false);
    }
  }

  async function handleProcess() {
    if (!sessionId) return;
    setProcessing(true);
    setError(null);

    try {
      const r = await axios.post('/api/packiyo/process', {
        session_id:    sessionId,
        locations:     locSelections,
        order_numbers: orderNumbers,
        sheets:        sheets,
      });
      setResults(r.data.results);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setProcessing(false);
    }
  }

  async function handleSubmit(r) {
    setSubmitState(prev => ({ ...prev, [r.packiyo_file]: 'submitting' }));
    try {
      await axios.post('/api/packiyo/submit', {
        session_id:   sessionId,
        packiyo_file: r.packiyo_file,
        notes:        noteValues[r.sheet] || '',
      });
      setSubmitState(prev => ({ ...prev, [r.packiyo_file]: 'done' }));
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      setSubmitState(prev => ({ ...prev, [r.packiyo_file]: { error: msg } }));
    }
  }

  // The add runs as a background job on the server (a big order outlasts a
  // single request); poll its status file until it settles.
  async function handleSubmitShopify(r) {
    const loc = locSelections[r.sheet];
    if (!loc?.id) return;
    const key = r.packiyo_file;
    setShopifyState(prev => ({ ...prev, [key]: { running: true } }));
    try {
      const resp = await axios.post('/api/packiyo/submit-shopify', {
        session_id:   sessionId,
        packiyo_file: key,
        location_id:  loc.id,
      });
      let st = resp.data;
      while (st.state === 'running') {
        const snap = st;
        setShopifyState(prev => ({ ...prev, [key]: { running: true, job: snap } }));
        await new Promise(res => setTimeout(res, 1500));
        st = (await axios.get(`/api/packiyo/shopify-status/${sessionId}/${encodeURIComponent(key)}`)).data;
      }
      setShopifyState(prev => ({ ...prev, [key]: { job: st } }));
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      setShopifyState(prev => ({ ...prev, [key]: { error: msg } }));
    }
  }

  function reset() {
    setOrderFile(null);
    clearDetection();
    setSubmitState({});
    setShopifyState({});
    setNoteValues({});
    setOrderNumbers({});
  }

  const inDetectPhase = sheets.length > 0 && !results.length;
  const locked        = detecting || inDetectPhase || processing || results.length > 0;

  return (
    <div className="pk-page">
      <h1>Packiyo Ordering</h1>
      <p className="pk-sub">
        Upload an order file (CSV or multi-sheet Excel). A Shopify location and order number are
        optional per order — skip the location and the import files are emailed to you instead of
        added to Shopify; leave the order number blank to auto-generate one.
      </p>

      {locsError && (
        <div className="pk-banner pk-banner--warn">
          Could not load Shopify locations: {locsError}
        </div>
      )}

      {/* Upload */}
      <div style={{ marginBottom: 20 }}>
        <label className="pk-label">Order File</label>
        <DropZone
          label="Drop order file here or browse"
          hint="CSV or Excel (.xlsx, .xls)"
          accept=".csv,.xlsx,.xls,.xlsm"
          file={orderFile}
          onFile={f => { setOrderFile(f); clearDetection(); }}
          locked={locked}
        />
      </div>

      {orderFile && !sheets.length && !results.length && (
        <div className="pk-actions">
          <button
            className="pk-btn-primary"
            onClick={handleDetect}
            disabled={detecting}
          >
            {detecting ? 'Detecting…' : 'Detect Orders'}
          </button>
        </div>
      )}

      {/* Sheet / location assignment */}
      {inDetectPhase && (
        <div className="pk-sheets">
          <h2 className="pk-section-title">
            {sheets.length} Order{sheets.length !== 1 ? 's' : ''} Detected
            {locsLoading && <span className="pk-loading-tag">Loading locations…</span>}
          </h2>

          {invalidSheets.length > 0 && (
            <div className="pk-banner pk-banner--warn" style={{ marginBottom: 14 }}>
              {invalidSheets.length} sheet{invalidSheets.length !== 1 ? 's' : ''} skipped
              (missing required headers): <strong>{invalidSheets.join(', ')}</strong>
            </div>
          )}

          <div className="pk-sheet-list">
            {sheets.map(s => (
              <div key={s.name} className="pk-sheet-card pk-sheet-card--stacked">
                <div className="pk-sheet-card-top">
                  <div className="pk-sheet-info">
                    <span className="pk-sheet-name">{s.name}</span>
                    <span className="pk-sheet-count">{s.row_count} SKU{s.row_count !== 1 ? 's' : ''}</span>
                    {s.shipping?.name && (
                      <span className="pk-sheet-contact">
                        {s.shipping.name}{s.shipping.company ? ` · ${s.shipping.company}` : ''}
                      </span>
                    )}
                    {s.shipping?.address && (
                      <span className="pk-sheet-address">{s.shipping.address}</span>
                    )}
                  </div>
                  <LocationSelect
                    locations={locations}
                    value={locSelections[s.name] || null}
                    onChange={loc => setLocSelections(prev => ({ ...prev, [s.name]: loc }))}
                  />
                </div>
                {!locSelections[s.name] && (
                  <div className="pk-banner pk-banner--warn" style={{ margin: '8px 0 0' }}>
                    No location selected — the import file will be emailed to you instead of
                    added to Shopify inventory.
                  </div>
                )}
                <input
                  className="pk-notes"
                  style={{ marginTop: 8 }}
                  placeholder="Order number (optional — auto-generated if left blank)…"
                  value={orderNumbers[s.name] || ''}
                  onChange={e => setOrderNumbers(prev => ({ ...prev, [s.name]: e.target.value }))}
                />
                <textarea
                  className="pk-notes"
                  placeholder="Order notes (optional)…"
                  value={noteValues[s.name] || ''}
                  onChange={e => setNoteValues(prev => ({ ...prev, [s.name]: e.target.value }))}
                  rows={2}
                />
              </div>
            ))}
          </div>

          <div className="pk-actions">
            <button className="pk-btn-secondary" onClick={reset}>Start Over</button>
            <button
              className="pk-btn-primary"
              onClick={handleProcess}
              disabled={processing || locsLoading}
            >
              {processing ? 'Generating…' : 'Generate Files'}
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="pk-results">
          <h2 className="pk-section-title">Files Ready</h2>
          {results.map(r => {
            const st  = submitState[r.packiyo_file];
            const sst = shopifyState[r.packiyo_file];
            const job = sst?.job;
            return (
              <div key={r.sheet} className="pk-result-card">
                <div className="pk-sheet-card pk-sheet-card--stacked" style={{ marginBottom: 12 }}>
                  <div className="pk-sheet-card-top">
                    <div className="pk-sheet-info">
                      <span className="pk-sheet-name">{r.sheet}</span>
                      <span className="pk-sheet-count">{r.row_count} SKU{r.row_count !== 1 ? 's' : ''} · {r.has_location ? r.location : 'No location — emailed'} · #{r.order_number}</span>
                      {r.shipping?.name && (
                        <span className="pk-sheet-contact">
                          {r.shipping.name}{r.shipping.company ? ` · ${r.shipping.company}` : ''}
                        </span>
                      )}
                      {r.shipping?.address && (
                        <span className="pk-sheet-address">{r.shipping.address}</span>
                      )}
                    </div>
                  </div>
                  {st !== 'done' && (
                    <textarea
                      className="pk-notes"
                      placeholder="Order notes (optional)…"
                      value={noteValues[r.sheet] || ''}
                      onChange={e => setNoteValues(prev => ({ ...prev, [r.sheet]: e.target.value }))}
                      rows={2}
                    />
                  )}
                  {st === 'done' && noteValues[r.sheet] && (
                    <div className="pk-notes-display">{noteValues[r.sheet]}</div>
                  )}
                  <div className="pk-result-downloads">
                    <a
                      className="pk-download-btn"
                      href={`/api/packiyo/download/${sessionId}/${r.packiyo_file}`}
                      download={r.packiyo_file}
                    >
                      ⬇ Packiyo Order
                    </a>
                    <a
                      className="pk-download-btn pk-download-btn--secondary"
                      href={`/api/packiyo/download/${sessionId}/${r.inventory_file}`}
                      download={r.inventory_file}
                    >
                      ⬇ Inventory Update
                    </a>
                    {st === 'done' ? (
                      <span className="pk-submit-ok">✓ Submitted</span>
                    ) : (
                      <button
                        className="pk-btn-submit"
                        onClick={() => handleSubmit(r)}
                        disabled={st === 'submitting'}
                      >
                        {st === 'submitting' ? 'Submitting…' : '↑ Submit to Packiyo'}
                      </button>
                    )}
                    {!r.has_location ? (
                      <span className="pk-submit-ok">
                        {r.emailed_to ? `✉ Emailed to ${r.emailed_to}` : '✉ No location — email not configured'}
                      </span>
                    ) : job?.state === 'done' ? (
                      <span className="pk-submit-ok">✓ Added to Shopify</span>
                    ) : (
                      <button
                        className="pk-btn-submit pk-btn-submit--secondary"
                        onClick={() => handleSubmitShopify(r)}
                        disabled={sst?.running}
                      >
                        {sst?.running
                          ? `Adding… ${job?.total ? `${job.adjusted.length}/${job.total}` : ''}`
                          : job || sst?.error ? '↻ Resume Shopify Add' : '+ Add to Shopify Inventory'}
                      </button>
                    )}
                  </div>
                </div>
                {st?.error && (
                  <div className="pk-error" style={{ marginTop: 8 }}>
                    Packiyo error: {st.error}
                  </div>
                )}
                {(sst?.error || job?.error) && (
                  <div className="pk-error" style={{ marginTop: 8 }}>
                    Shopify inventory error: {sst?.error || job.error}
                  </div>
                )}
                {job?.failed?.length > 0 && !sst?.running && (
                  <div className="pk-error" style={{ marginTop: 8 }}>
                    {job.failed.length} SKU{job.failed.length !== 1 ? 's' : ''} failed — Resume retries only these:
                    {job.failed.map(f => <div key={f.sku}>{f.sku} (+{f.quantity}): {f.error}</div>)}
                  </div>
                )}
                {job && !sst?.running && job.state !== 'running' && (
                  <div className="pk-notes-display" style={{ marginTop: 8 }}>
                    Added {job.adjusted.length}{job.total ? ` of ${job.total}` : ''} SKU{job.adjusted.length !== 1 ? 's' : ''} to {job.location_name}
                    {job.skipped_skus?.length > 0 && (
                      <div>Not found in Shopify: {job.skipped_skus.join(', ')}</div>
                    )}
                    {job.unmapped_bundles?.length > 0 && (
                      <div>No bundle mapping found: {job.unmapped_bundles.join(', ')}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <div className="pk-actions" style={{ marginTop: 24 }}>
            <button className="pk-btn-secondary" onClick={reset}>Process New Files</button>
          </div>
        </div>
      )}

      {error && <div className="pk-error">{error}</div>}
    </div>
  );
}
